import { faArrowLeft, faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { useWatch } from 'react-hook-form';
import Group from '~/components/Tools/ToolPanelFormGroup';
import AmountField from '~/components/forms/Tools/CalculateMaximumMortgage/AmountField';
import DoYouHaveMonthlyObligationsField from '~/components/forms/Tools/CalculateMaximumMortgage/DoYouHaveMonthlyObligationsField';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '~/components/ui/Accordion';
import { Button } from '~/components/ui/Button';
import { useCalculateMaximumMortgageCalculation } from '~/pages/Tools/CalculateMaximumMortgage/Calculate/context/CalculationContext';
import { useCalculateMaximumMortgageForm } from '~/pages/Tools/CalculateMaximumMortgage/Calculate/context/FormProvider';

type ObligationsStepProps = {
  hasResult: boolean;
  onHandlePrev: () => void;
};

const ObligationsStep: React.FC<ObligationsStepProps> = ({
  onHandlePrev,
  hasResult,
}) => {
  const { control } = useCalculateMaximumMortgageForm();

  const hasPartner = useWatch({
    control,
    name: 'hasPartner',
    defaultValue: false,
  });

  const hasObligations = useWatch({
    control,
    name: 'hasObligations',
    defaultValue: false,
  });

  const { isCalculating, setHasCalculated } =
    useCalculateMaximumMortgageCalculation();

  const handlePrev = () => {
    onHandlePrev();
    setHasCalculated(false);
  };

  return (
    <>
      <div>
        <Group>
          <DoYouHaveMonthlyObligationsField
            name="hasObligations"
            label={`${hasPartner ? 'Hebben jullie' : 'Heb jij'} maandelijkse verplichtingen?`}
            description="Hieronder valt o.a. partner alimentatie, (studie)schulden of kredieten. Vul alleen in wat van toepassing is."
          />
        </Group>
        {hasObligations && (
          <Group>
            <Accordion type="multiple">
              <AccordionItem value="loans">
                <AccordionTrigger className="text-lg text-blue-600">
                  Leningen
                </AccordionTrigger>
                <AccordionContent>
                  <AmountField
                    name="loans"
                    label={`Hoeveel ${hasPartner ? 'betalen jullie' : 'betaal je'} maandelijks aan de aflossing van ${hasPartner ? 'jullie' : 'je'} lening, inclusief rente?`}
                    placeholder="Maandbedrag"
                  />
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="alimony">
                <AccordionTrigger className="text-lg text-blue-600">
                  Partneralimentatie
                </AccordionTrigger>
                <AccordionContent>
                  <AmountField
                    name="alimony"
                    label={`Hoeveel ${hasPartner ? 'betalen jullie' : 'betaal je'} maandelijks aan partneralimentatie?`}
                    placeholder="Maandbedrag"
                  />
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="studentDebt">
                <AccordionTrigger className="text-lg text-blue-600">
                  Studieschuld
                </AccordionTrigger>
                <AccordionContent>
                  <AmountField
                    name="studentDebt"
                    label={`Hoeveel ${hasPartner ? 'moeten jullie' : 'moet je'} maandelijks (gaan) aflossen op de studieschuld?`}
                    description="Vul hier het wettelijke maandbedrag in. Niet het bedrag dat betaald wordt op basis van het huidige inkomen."
                    placeholder="Maandbedrag"
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Group>
        )}
      </div>
      <div className="mt-8 flex items-center justify-between">
        <Button variant="default" type="button" onClick={handlePrev}>
          <FontAwesomeIcon icon={faArrowLeft} />
          Vorige
        </Button>

        <Button
          disabled={isCalculating}
          variant={isCalculating ? 'disabled' : 'primary'}
          type="submit"
        >
          <FontAwesomeIcon icon={faCheck} />
          {hasResult ? 'Herberekenen' : 'Berekenen'}
        </Button>
      </div>
    </>
  );
};

export default ObligationsStep;
